import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AvailabilityException,
  AvailabilityExceptionCreatedEnvelope,
  AvailabilityExceptionDeletedEnvelope,
  AvailabilityExceptionQuery,
  AvailabilityExceptionUpdatedEnvelope,
  AvailabilityExceptionsQueryBuilder,
  BulkCreateAvailabilityExceptionsOptions,
  BulkCreateAvailabilityExceptionsResponse,
  BulkUpdateAvailabilityExceptionTagsApplicationErrors,
  BulkUpdateAvailabilityExceptionTagsByFilterApplicationErrors,
  BulkUpdateAvailabilityExceptionTagsByFilterOptions,
  BulkUpdateAvailabilityExceptionTagsByFilterResponse,
  BulkUpdateAvailabilityExceptionTagsOptions,
  BulkUpdateAvailabilityExceptionTagsResponse,
  BulkUpdateAvailabilityExceptionsOptions,
  BulkUpdateAvailabilityExceptionsResponse,
  MaskedAvailabilityException,
  QueryAvailabilityExceptionsResponse,
  UpdateAvailabilityException,
  bulkCreateAvailabilityExceptions as universalBulkCreateAvailabilityExceptions,
  bulkUpdateAvailabilityExceptionTags as universalBulkUpdateAvailabilityExceptionTags,
  bulkUpdateAvailabilityExceptionTagsByFilter as universalBulkUpdateAvailabilityExceptionTagsByFilter,
  bulkUpdateAvailabilityExceptions as universalBulkUpdateAvailabilityExceptions,
  createAvailabilityException as universalCreateAvailabilityException,
  deleteAvailabilityException as universalDeleteAvailabilityException,
  getAvailabilityException as universalGetAvailabilityException,
  queryAvailabilityExceptions as universalQueryAvailabilityExceptions,
  typedQueryAvailabilityExceptions as universalTypedQueryAvailabilityExceptions,
  updateAvailabilityException as universalUpdateAvailabilityException,
} from './restaurants-v1-availability-exception-availability-exceptions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function createAvailabilityException(
  httpClient: HttpClient
): CreateAvailabilityExceptionSignature {
  return (
    availabilityException: NonNullablePaths<
      AvailabilityException,
      | `affectedFulfillmentMethods`
      | `affectedFulfillmentMethods.affectedMethods`
      | `available`
      | `endTime`
      | `operationId`
      | `startTime`,
      3
    >
  ) =>
    universalCreateAvailabilityException(
      availabilityException,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateAvailabilityExceptionSignature {
  /**
   * Creates a new availability exception.
   * @param - Availability exception details.
   * @returns The created availability exception.
   */
  (
    availabilityException: NonNullablePaths<
      AvailabilityException,
      | `affectedFulfillmentMethods`
      | `affectedFulfillmentMethods.affectedMethods`
      | `available`
      | `endTime`
      | `operationId`
      | `startTime`,
      3
    >
  ): Promise<
    NonNullablePaths<
      AvailabilityException,
      | `affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds`
      | `affectedFulfillmentMethods.affectedMethods`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function getAvailabilityException(
  httpClient: HttpClient
): GetAvailabilityExceptionSignature {
  return (availabilityExceptionId: string) =>
    universalGetAvailabilityException(
      availabilityExceptionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAvailabilityExceptionSignature {
  /**
   * Retrieves an availability exception.
   * @param - ID of the availability exception to retrieve.
   * @returns The requested availability exception.
   */
  (availabilityExceptionId: string): Promise<
    NonNullablePaths<
      AvailabilityException,
      | `affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds`
      | `affectedFulfillmentMethods.affectedMethods`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function updateAvailabilityException(
  httpClient: HttpClient
): UpdateAvailabilityExceptionSignature {
  return (
    _id: string,
    availabilityException: NonNullablePaths<
      UpdateAvailabilityException,
      `revision`,
      2
    >
  ) =>
    universalUpdateAvailabilityException(
      _id,
      availabilityException,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateAvailabilityExceptionSignature {
  /**
   * Updates an availability exception.
   *
   * Each time the availability exception is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the availability exception.
   * This ensures you're working with the latest availability exception and prevents unintended overwrites.
   * @param - Availability exception ID.
   * @returns Updated availability exception.
   */
  (
    _id: string,
    availabilityException: NonNullablePaths<
      UpdateAvailabilityException,
      `revision`,
      2
    >
  ): Promise<
    NonNullablePaths<
      AvailabilityException,
      | `affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds`
      | `affectedFulfillmentMethods.affectedMethods`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function deleteAvailabilityException(
  httpClient: HttpClient
): DeleteAvailabilityExceptionSignature {
  return (availabilityExceptionId: string) =>
    universalDeleteAvailabilityException(
      availabilityExceptionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteAvailabilityExceptionSignature {
  /**
   * Deletes an availability exception.
   * @param - ID of the availability exception to delete.
   */
  (availabilityExceptionId: string): Promise<void>;
}

export function queryAvailabilityExceptions(
  httpClient: HttpClient
): QueryAvailabilityExceptionsSignature {
  return () =>
    universalQueryAvailabilityExceptions(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryAvailabilityExceptionsSignature {
  /**
   * Creates a query to retrieve a list of availability exceptions.
   *
   * The `queryAvailabilityExceptions()` function builds a query to retrieve a list of availability exceptions and returns an `AvailabilityExceptionsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](/availability-exceptions/availability-exceptions-query-builder/find) function.
   *
   * You can refine the query by chaining `AvailabilityExceptionsQueryBuilder` functions onto the query. `AvailabilityExceptionsQueryBuilder` functions enable you to filter, sort, and control the results that `queryAvailabilityExceptions()` returns.
   *
   * `queryAvailabilityExceptions()` runs with the following `AvailabilityExceptionsQueryBuilder` defaults, which you can override:
   *
   * - [`limit(50)`](/availability-exceptions/availability-exceptions-query-builder/limit)
   * - [`ascending('_id')`](/availability-exceptions/availability-exceptions-query-builder/ascending)
   *
   * The following `AvailabilityExceptionsQueryBuilder` functions are supported for `queryAvailabilityExceptions()`. For a full description of the availability exception object, see the object returned for the [`items`](/availability-exceptions/availability-exceptions-query-result/items) property in `AvailabilityExceptionsQueryResult`.
   */
  (): AvailabilityExceptionsQueryBuilder;
}

export function typedQueryAvailabilityExceptions(
  httpClient: HttpClient
): TypedQueryAvailabilityExceptionsSignature {
  return (query: AvailabilityExceptionQuery) =>
    universalTypedQueryAvailabilityExceptions(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryAvailabilityExceptionsSignature {
  /** */
  (query: AvailabilityExceptionQuery): Promise<
    NonNullablePaths<
      QueryAvailabilityExceptionsResponse,
      | `availabilityExceptions`
      | `availabilityExceptions.${number}.affectedFulfillmentMethods.affectedMethods`,
      5
    >
  >;
}

export function bulkCreateAvailabilityExceptions(
  httpClient: HttpClient
): BulkCreateAvailabilityExceptionsSignature {
  return (
    availabilityExceptions: NonNullablePaths<
      AvailabilityException,
      | `affectedFulfillmentMethods`
      | `affectedFulfillmentMethods.affectedMethods`
      | `available`
      | `endTime`
      | `operationId`
      | `startTime`,
      3
    >[],
    options?: BulkCreateAvailabilityExceptionsOptions
  ) =>
    universalBulkCreateAvailabilityExceptions(
      availabilityExceptions,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateAvailabilityExceptionsSignature {
  /**
   * Creates multiple availability exceptions.
   * @param - Availability exception details.
   */
  (
    availabilityExceptions: NonNullablePaths<
      AvailabilityException,
      | `affectedFulfillmentMethods`
      | `affectedFulfillmentMethods.affectedMethods`
      | `available`
      | `endTime`
      | `operationId`
      | `startTime`,
      3
    >[],
    options?: BulkCreateAvailabilityExceptionsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateAvailabilityExceptionsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.availabilityException.affectedFulfillmentMethods.affectedMethods`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateAvailabilityExceptions(
  httpClient: HttpClient
): BulkUpdateAvailabilityExceptionsSignature {
  return (
    availabilityExceptions: NonNullablePaths<
      MaskedAvailabilityException,
      `availabilityException._id` | `availabilityException.revision`,
      3
    >[],
    options?: BulkUpdateAvailabilityExceptionsOptions
  ) =>
    universalBulkUpdateAvailabilityExceptions(
      availabilityExceptions,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateAvailabilityExceptionsSignature {
  /**
   * Updates multiple availability exceptions.
   * @param - Availability exceptions entities to update.
   */
  (
    availabilityExceptions: NonNullablePaths<
      MaskedAvailabilityException,
      `availabilityException._id` | `availabilityException.revision`,
      3
    >[],
    options?: BulkUpdateAvailabilityExceptionsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateAvailabilityExceptionsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.affectedFulfillmentMethods.affectedMethods`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateAvailabilityExceptionTags(
  httpClient: HttpClient
): BulkUpdateAvailabilityExceptionTagsSignature {
  return (
    availabilityExceptionIds: string[],
    options?: BulkUpdateAvailabilityExceptionTagsOptions
  ) =>
    universalBulkUpdateAvailabilityExceptionTags(
      availabilityExceptionIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateAvailabilityExceptionTagsSignature {
  /**
   * Synchronously update tags on multiple availability exceptions.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - IDs of the availability exceptions to update tags for.
   */
  (
    availabilityExceptionIds: string[],
    options?: BulkUpdateAvailabilityExceptionTagsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateAvailabilityExceptionTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateAvailabilityExceptionTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateAvailabilityExceptionTagsByFilter(
  httpClient: HttpClient
): BulkUpdateAvailabilityExceptionTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateAvailabilityExceptionTagsByFilterOptions
  ) =>
    universalBulkUpdateAvailabilityExceptionTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateAvailabilityExceptionTagsByFilterSignature {
  /**
   * Asynchronously update tags on multiple availability exceptions according to the specified filter.
   * If a filter isn't specified, this method updates all availability exceptions.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - Filter that determines which availability exceptions to update tags for.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateAvailabilityExceptionTagsByFilterOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateAvailabilityExceptionTagsByFilterResponse,
      `jobId`,
      2
    > & {
      __applicationErrorsType?: BulkUpdateAvailabilityExceptionTagsByFilterApplicationErrors;
    }
  >;
}

export const onAvailabilityExceptionCreated = EventDefinition(
  'wix.restaurants.v1.availability_exception_created',
  true,
  (event: AvailabilityExceptionCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.startTime' },
            { path: 'entity.endTime' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<AvailabilityExceptionCreatedEnvelope>();
export const onAvailabilityExceptionDeleted = EventDefinition(
  'wix.restaurants.v1.availability_exception_deleted',
  true,
  (event: AvailabilityExceptionDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.startTime' },
            { path: 'entity.endTime' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<AvailabilityExceptionDeletedEnvelope>();
export const onAvailabilityExceptionUpdated = EventDefinition(
  'wix.restaurants.v1.availability_exception_updated',
  true,
  (event: AvailabilityExceptionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.startTime' },
            { path: 'entity.endTime' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.startTime' },
            { path: 'modifiedFields.endTime' },
          ],
        },
      ])
    )
)<AvailabilityExceptionUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AffectedFulfillmentMethods,
  AffectedFulfillmentMethodsAffectedMethodsOptionsOneOf,
  AffectedMethods,
  ApplicationError,
  AvailabilityException,
  AvailabilityExceptionCreatedEnvelope,
  AvailabilityExceptionDeletedEnvelope,
  AvailabilityExceptionQuerySpec,
  AvailabilityExceptionUpdatedEnvelope,
  AvailabilityExceptionsQueryBuilder,
  AvailabilityExceptionsQueryResult,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkAvailabilityExceptionResult,
  BulkCreateAvailabilityExceptionsOptions,
  BulkCreateAvailabilityExceptionsRequest,
  BulkCreateAvailabilityExceptionsResponse,
  BulkCreateAvailabilityExceptionsResult,
  BulkUpdateAvailabilityExceptionTagsByFilterOptions,
  BulkUpdateAvailabilityExceptionTagsByFilterRequest,
  BulkUpdateAvailabilityExceptionTagsByFilterResponse,
  BulkUpdateAvailabilityExceptionTagsOptions,
  BulkUpdateAvailabilityExceptionTagsRequest,
  BulkUpdateAvailabilityExceptionTagsResponse,
  BulkUpdateAvailabilityExceptionTagsResult,
  BulkUpdateAvailabilityExceptionsOptions,
  BulkUpdateAvailabilityExceptionsRequest,
  BulkUpdateAvailabilityExceptionsResponse,
  CreateAvailabilityExceptionRequest,
  CreateAvailabilityExceptionResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteAvailabilityExceptionRequest,
  DeleteAvailabilityExceptionResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetAvailabilityExceptionRequest,
  GetAvailabilityExceptionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MaskedAvailabilityException,
  MessageEnvelope,
  QueryAvailabilityExceptionsRequest,
  QueryAvailabilityExceptionsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  SpecificFulfillmentMethodsImpactScope,
  TagList,
  Tags,
  Type,
  UpdateAvailabilityException,
  UpdateAvailabilityExceptionRequest,
  UpdateAvailabilityExceptionResponse,
  WebhookIdentityType,
  utils,
} from './restaurants-v1-availability-exception-availability-exceptions.universal.js';
